/**
 * @fileoverview Hook for the system-web Customer Portal configuration surface.
 * @module system-web/hooks/usePortalConfig
 *
 * Responsibilities:
 * - Subscribe to the cached portal config via liveQuery (pure read).
 * - Trigger the one-shot online refresh on mount (separate effect per the
 *   liveQuery contract).
 *
 * Dependencies: portalConfigService, dexie/liveQuery.
 *
 * @hook usePortalConfig
 * Purpose:     System-default portal config for /portal-config.
 * Returns:     { config, isLoading, error }
 * Side Effects: mount — liveQuery subscription + refreshPortalDefaults().
 * Offline Behavior: last-cached config is readable offline; save requires
 *   connectivity.
 */
import { useEffect, useState } from 'react';
import { liveQuery } from 'dexie';
import {
  getCachedPortalDefaults,
  refreshPortalDefaults,
  type PortalConfigBundle,
} from '../services/portalConfigService';

export function usePortalConfig() {
  const [bundle, setBundle] = useState<PortalConfigBundle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const sub = liveQuery(() => getCachedPortalDefaults()).subscribe({
      next: (row) => {
        setBundle(row);
        setIsLoading(false);
      },
      error: (err) => {
        console.error('[usePortalConfig] liveQuery error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load portal defaults');
        setIsLoading(false);
      },
    });
    refreshPortalDefaults().catch((err) => {
      console.error('[usePortalConfig] refresh failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh portal defaults');
    });
    return () => sub.unsubscribe();
  }, []);

  return { config: bundle?.config ?? null, isLoading, error };
}
